import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const GREEN = [22, 163, 74];      // #16A34A
const DARK = [10, 37, 24];         // #0A2518
const LIGHT_GREEN = [240, 253, 244]; // #F0FDF4
const GRAY = [107, 114, 128];      // #6B7280

const fmtXof = (n) => `${Number(n || 0).toLocaleString("fr-FR")} FCFA`;

/**
 * Build and download a PDF for a monthly parent report.
 * @param {object} report - response from GET /api/reports/monthly
 */
export function downloadMonthlyReportPdf(report) {
  if (!report) return;

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;
  let y = margin;

  // ---------- Header bar ----------
  doc.setFillColor(...GREEN);
  doc.rect(0, 0, pageW, 70, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("MonRépétiteur", margin, 35);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Plateforme de soutien scolaire au Bénin", margin, 52);

  // Date generated, top right
  const todayStr = new Date().toLocaleDateString("fr-FR", {
    day: "2-digit", month: "long", year: "numeric",
  });
  doc.setFontSize(9);
  doc.text(`Édité le ${todayStr}`, pageW - margin, 35, { align: "right" });

  y = 100;

  // ---------- Title ----------
  doc.setTextColor(...DARK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Rapport mensuel", margin, y);
  y += 18;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...GRAY);
  const period = report.period_label
    ? report.period_label[0].toUpperCase() + report.period_label.slice(1)
    : "";
  doc.text(`Famille : ${report.parent_name || ""} · Période : ${period}`, margin, y);
  y += 24;

  // ---------- KPI tiles ----------
  const tileW = (pageW - margin * 2 - 16) / 3;
  const tiles = [
    { label: "Heures validées", value: `${(report.totals?.hours || 0).toFixed(1)} h` },
    { label: "Séances", value: `${report.totals?.sessions_count || 0}` },
    { label: "Investissement", value: fmtXof(report.totals?.amount_xof) },
  ];
  tiles.forEach((t, i) => {
    const x = margin + i * (tileW + 8);
    doc.setFillColor(...LIGHT_GREEN);
    doc.setDrawColor(...GREEN);
    doc.roundedRect(x, y, tileW, 60, 8, 8, "FD");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(21, 128, 61);
    doc.text(t.label.toUpperCase(), x + 12, y + 18);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(...DARK);
    doc.text(t.value, x + 12, y + 42);
  });
  y += 80;

  // ---------- By educator ----------
  if (report.by_educator?.length) {
    autoTable(doc, {
      startY: y,
      head: [["Éducateur", "Heures", "Séances", "Montant"]],
      body: report.by_educator.map((e) => [
        e.educator_name,
        `${e.hours.toFixed(1)} h`,
        `${e.sessions}`,
        fmtXof(e.amount_xof),
      ]),
      headStyles: { fillColor: GREEN, textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 10, textColor: DARK },
      alternateRowStyles: { fillColor: [250, 252, 251] },
      margin: { left: margin, right: margin },
      styles: { cellPadding: 6 },
      didDrawPage: () => {},
    });
    y = doc.lastAutoTable.finalY + 20;
  }

  // ---------- By subject ----------
  if (report.by_subject?.length) {
    autoTable(doc, {
      startY: y,
      head: [["Matière", "Heures", "Séances", "Montant"]],
      body: report.by_subject.map((s) => [
        s.subject,
        `${s.hours.toFixed(1)} h`,
        `${s.sessions}`,
        fmtXof(s.amount_xof),
      ]),
      headStyles: { fillColor: GREEN, textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 10, textColor: DARK },
      alternateRowStyles: { fillColor: [250, 252, 251] },
      margin: { left: margin, right: margin },
      styles: { cellPadding: 6 },
    });
    y = doc.lastAutoTable.finalY + 20;
  }

  // ---------- Detailed sessions ----------
  if (report.sessions?.length) {
    if (y > pageH - 200) {
      doc.addPage();
      y = margin;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...DARK);
    doc.text("Détail des séances validées", margin, y);
    y += 10;
    autoTable(doc, {
      startY: y,
      head: [["Date", "Matière", "Éducateur", "Heures", "Montant"]],
      body: report.sessions.map((s) => [
        s.date,
        s.subject,
        s.educator_name,
        `${s.hours.toFixed(1)} h`,
        fmtXof(s.amount_xof),
      ]),
      headStyles: { fillColor: GREEN, textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: DARK },
      alternateRowStyles: { fillColor: [250, 252, 251] },
      margin: { left: margin, right: margin },
      styles: { cellPadding: 5 },
    });
  }

  // ---------- Sharing CTA + Footer on every page ----------
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    const fy = pageH - 50;
    doc.setDrawColor(229, 235, 231);
    doc.line(margin, fy, pageW - margin, fy);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(...GREEN);
    doc.text(
      "Vous aimez MonRépétiteur ? Partagez à une amie sur monrepetiteur.org",
      pageW / 2, fy + 16, { align: "center" }
    );
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GRAY);
    doc.setFontSize(8);
    doc.text(
      "MonRépétiteur · Cotonou, Bénin · Un projet de l'ONG YES",
      pageW / 2, fy + 30, { align: "center" }
    );
    doc.text(`Page ${p}/${pageCount}`, pageW - margin, fy + 30, { align: "right" });
  }

  const safePeriod = (report.period_label || "rapport").replace(/\s+/g, "-");
  doc.save(`MonRepetiteur_${safePeriod}.pdf`);
}
